import { useLocation } from '@tanstack/react-router';
import { z } from 'zod';

export type InspectTab = (typeof inspectTabKeys)[number];
export const inspectTabKeys = ['indexes', 'shapes', 'slow-queries'] as const;

export type InspectTabsSearchParams = z.infer<typeof InspectTabsSearchParams>;

/**
 * Search params of the inspect pages, which exist on the global (`/inspect`), database
 * (`/inspect/<db>`) and collection (`/inspect/<db>/<coll>`) level: the cross-tab filters,
 * rendered as dismissable chips. The active tab and selection live in the location hash instead
 * (see inspectHash) — as one value they cannot leave stale combinations behind, and they stay out
 * of the way of search-param navigation and active-link matching. Table-internal state (sort,
 * per-column filters) deliberately stays out of the URL.
 */
export const InspectTabsSearchParams = z.object({
  /** only show shapes using this index, e.g. when jumping to the shapes tab from an index */
  indexFilter: z.string().optional(),
  /**
   * only show slow queries of one query shape, e.g. when jumping to the slow queries tab from a
   * shape; carries the shape's raw queryShapeHashes (one per concrete db), since the merged shape
   * id is not stored on the slow query items
   */
  shapeFilter: z
    .string()
    .array()
    .or(z.string().transform((value) => [value]))
    .optional(),
});

/** Search updater that patches the inspect params while keeping everything else */
export function updateInspectSearch(patch: Partial<InspectTabsSearchParams>) {
  return (prev: Record<string, unknown>): Record<string, unknown> => ({ ...prev, ...patch });
}

/**
 * Builds the location hash addressing a tab and optionally a selected item on it, e.g.
 * `shapes/<shape id>` — the default tab without selection is the empty hash. Doubles as the
 * element id of the details section, so the browser/router scroll to it.
 */
export function inspectHash(tab: InspectTab, selection?: string): string {
  if (selection !== undefined) {
    return `${tab}/${selection}`;
  }

  return tab === 'indexes' ? '' : tab;
}

export function parseInspectHash(hash: string): { tab: InspectTab; selection?: string } {
  // only the first segment is the tab; a selection may itself contain slashes
  const [tab = '', ...rest] = hash.split('/');
  const selection = rest.length > 0 ? rest.join('/') : undefined;

  if (inspectTabKeys.includes(tab as InspectTab)) {
    return { tab: tab as InspectTab, selection };
  }

  return { tab: 'indexes' };
}

/** The active tab and selection, parsed from the location hash */
export function useInspectHash(): { tab: InspectTab; selection?: string } {
  const hash = useLocation({ select: (location) => location.hash });
  return parseInspectHash(hash);
}
